// Texas Christian University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - TCU's Common Data Set 2025-2026 (CDS-2025-26-TCU.pdf), linked from
//    Institutional Research's Common Data Set page: C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table (which has an
//    "Unknown" column: 163 applied, 87 admitted), C8 test policy for Fall
//    2027, C21/C22 early plans, G1/G5 2026-2027 costs. C21 gives one early
//    decision count, not split into ED I and ED II, and C22 gives no early
//    action counts.
//  - admissions.tcu.edu: Apply as a First-Year Student (checklist and the
//    dates and deadlines table, which carries no year; it is the page as
//    posted for the application now open), the SAT/ACT testing policy (TCU
//    is test-optional "through 2028", for students graduating high school
//    through 2028), and the Nursing Supplement.
//  - finearts.tcu.edu: the School of Music undergraduate audition page, the
//    Department of Theatre and the School for Classical & Contemporary Dance
//    apply and audition pages (the dance page lists Fall 2027 entry dates).
//  - College Scorecard, school id 228875 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirks: C21 lists the Early Decision II notification date as 3/15; the
// admissions page says ED II decisions come by March 1 with a commitment by
// March 15. The page uses the admissions page. The CDS G4 says tuition varies
// by program, and 34% of full-time undergraduates pay more than the G1
// tuition. The cost total is the sum of the G1/G5 items.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'TCU Common Data Set 2025-2026',
        url: 'https://ir.tcu.edu/facts-data/common-data-set/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'TCU Admission, Apply as a First-Year Student',
        url: 'https://admissions.tcu.edu/apply/first-year/index.php',
        read: '2026-10-01',
    },
    tests: {
        label: 'TCU Admission, Standardized Tests (ACT/SAT) for First-Year Admission',
        url: 'https://admissions.tcu.edu/apply/first-year/testing-policy-sat-act.php',
        read: '2026-10-01',
    },
    nursing: {
        label: 'TCU Admission, Nursing Supplement',
        url: 'https://admissions.tcu.edu/apply/first-year/nursing-supplement.php',
        read: '2026-10-01',
    },
    music: {
        label: 'TCU School of Music, Undergraduate Apply and Audition',
        url: 'https://finearts.tcu.edu/music/apply-audition/undergraduate/index.php',
        read: '2026-10-01',
    },
    theatre: {
        label: 'TCU Department of Theatre, Apply and Audition',
        url: 'https://finearts.tcu.edu/theatre/apply-audition/index.php',
        read: '2026-10-01',
    },
    dance: {
        label: 'TCU School for Classical & Contemporary Dance, Apply and Audition',
        url: 'https://finearts.tcu.edu/dance/apply-audition/index.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?228875-Texas-Christian-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'texas-christian-university',
    kbId: 'texas_christian_university',
    name: 'Texas Christian University',
    shortName: 'TCU',
    aliases: ['TCU'],
    location: 'Fort Worth, TX',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (TCU brand colors); decoration only.
    branding: {
        primary: '#4D1979',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/texas_christian_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 22680,
        admitted: 10906,
        enrolled: 2754,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 10757, admitted: 4663 },
            { group: 'Out-of-state', applied: 10483, admitted: 5818 },
            { group: 'International', applied: 1277, admitted: 338 },
            { group: 'Unknown', applied: 163, admitted: 87 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'TCU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set gives one early decision count for fall 2025, not split into Early Decision I and II, and no early action counts.',
        rows: [
            { plan: 'Early Decision I and II', applied: 1373, admitted: 935 },
        ],
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to TCU through one application. Some majors ask for more:',
        rows: [
            { school: 'Nursing', requirement: 'Nursing applicants answer two supplemental questions through an online form linked in the MyTCU portal after they apply: due November 15 for Early Action and Early Decision I, February 15 for Regular Decision and Early Decision II.', source: 'nursing' },
            { school: 'School of Music', requirement: 'After you submit the TCU application, upload a prescreening video through the School of Music application. If selected, you are invited to audition.', source: 'music' },
            { school: 'Department of Theatre', requirement: 'BFA programs require an audition (Acting, Musical Theatre) or a portfolio review and interview (Design & Technology); Theatre Studies applicants choose either. Each starts with a prescreen on Acceptd. The BA program asks for an Acceptd prescreen and an interview.', source: 'theatre' },
            { school: 'School for Classical & Contemporary Dance', requirement: 'Every applicant submits materials on Acceptd and auditions. For fall 2027 entry, in-person auditions are November 13, 2026, January 29, 2027 and February 12, 2027, each with an application due four days before.', source: 'dance' },
        ],
        note: 'For theatre and dance, you must also be admitted to TCU before the department\'s acceptance is final.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 1', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'By January 1', binding: true },
            { plan: 'Early Decision II', deadline: 'February 1', decision: 'By March 1', binding: true },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By April 1', binding: false },
        ],
        note: 'Early Action and Regular Decision admits pay the commitment deposit by May 1. For the Chancellor\'s Scholars program, TCU recommends applying by November 1 and to the John V. Roach Honors College by November 15.',
        source: 'firstYear',
        binding: 'Early Decision is binding and for students whose top choice is TCU: you submit the Early Decision Agreement with the application. Early Decision I admits commit by January 15; Early Decision II admits commit by March 15. Early Action is not binding: you have until May 1 to decide.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'TCU is test-optional through 2028: the SAT or ACT is not required for students graduating high school through 2028. If you send scores, TCU superscores each test and uses the better of the SAT or ACT, and under its "Do No Harm" policy it leaves out a submitted score that would hurt your admission or scholarship review. You may send unofficial scores yourself (official scores are needed only if you enroll); tests taken after February of senior year are not.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by March 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 66520 },
            { item: 'Required fees', amount: 90 },
            { item: 'Food and housing (on campus)', amount: 18780 },
            { item: 'Books and supplies', amount: 700 },
            { item: 'Transportation', amount: 1374 },
            { item: 'Other expenses', amount: 2278 },
        ],
        total: 89742,
        note: 'For a full-time undergraduate living on campus. Tuition varies by program: the Common Data Set says 34% of full-time undergraduates pay more than this tuition.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 35045,
            byIncome: [
                { income: '$0 to $30,000', amount: 22363 },
                { income: '$30,001 to $48,000', amount: 12610 },
                { income: '$48,001 to $75,000', amount: 19790 },
                { income: '$75,001 to $110,000', amount: 34094 },
                { income: 'Over $110,000', amount: 54791 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from TCU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
